import React from 'react';
import { LuLoaderCircle } from 'react-icons/lu';

// Primary actions use brand-600 (white text passes AA contrast); every other action is neutral.
const variantClasses = {
  primary: 'bg-brand-600 text-white shadow-sm hover:bg-brand-700',
  secondary: 'border border-ui-input bg-ui-card text-ui-ink shadow-sm hover:bg-ui-hover',
  outline: 'border border-brand-600 bg-transparent text-brand-700 hover:bg-brand-50 dark:border-brand-400 dark:text-brand-300 dark:hover:bg-brand-500/10',
  ghost: 'bg-transparent text-ui-ink-2 hover:bg-ui-hover hover:text-ui-ink',
  danger: 'bg-red-600 text-white shadow-sm hover:bg-red-700',
  success: 'bg-emerald-600 text-white shadow-sm hover:bg-emerald-700',
  warning: 'bg-amber-400 text-neutral-900 shadow-sm hover:bg-amber-500',
  info: 'bg-blue-600 text-white shadow-sm hover:bg-blue-700',
};

// Older variant names map onto the neutral secondary button
variantClasses.accent = variantClasses.secondary;
variantClasses.cancel = variantClasses.secondary;

const sizeClasses = {
  xs: 'h-7 gap-1.5 px-2.5 text-xs',
  sm: 'h-8 gap-1.5 px-3 text-[13px]',
  md: 'h-9 gap-2 px-4 text-sm',
  lg: 'h-10 gap-2 px-5 text-sm',
  xl: 'h-11 gap-2 px-6 text-base',
};

/**
 * The one button of the dashboard.
 *
 * @param {Object} props - Button properties
 * @param {string} props.variant - primary, secondary, outline, ghost, danger, success, warning, info
 * @param {string} props.size - xs, sm, md, lg, xl
 * @param {boolean} props.loading - Shows a spinner and blocks clicks
 * @param {boolean} props.fullWidth - Stretches to the width of its container
 * @param {React.ReactNode} props.leftIcon - Icon before the label
 * @param {React.ReactNode} props.rightIcon - Icon after the label
 * @param {string} props.href - Renders a link styled as a button
 */
const Button = ({
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  fullWidth = false,
  children,
  leftIcon,
  rightIcon,
  className = '',
  onClick,
  type = 'button',
  href,
  target,
  ...props
}) => {
  const isBlocked = disabled || loading;

  const classes = [
    'inline-flex shrink-0 items-center justify-center rounded-lg font-medium whitespace-nowrap',
    'transition-colors duration-150 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50',
    variantClasses[variant] || variantClasses.primary,
    sizeClasses[size] || sizeClasses.md,
    fullWidth ? 'w-full' : '',
    isBlocked && href ? 'pointer-events-none opacity-50' : '',
    className,
  ].filter(Boolean).join(' ');

  const content = (
    <>
      {loading ? (
        <LuLoaderCircle aria-hidden className="size-4 animate-spin" />
      ) : (
        leftIcon && <span aria-hidden className="flex shrink-0 items-center">{leftIcon}</span>
      )}
      {children}
      {rightIcon && <span aria-hidden className="flex shrink-0 items-center">{rightIcon}</span>}
    </>
  );

  if (href) {
    return (
      <a
        href={isBlocked ? undefined : href}
        target={target}
        rel={target === '_blank' ? 'noopener noreferrer' : undefined}
        className={classes}
        onClick={onClick}
        {...props}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type={type}
      className={classes}
      disabled={isBlocked}
      aria-busy={loading || undefined}
      onClick={onClick}
      {...props}
    >
      {content}
    </button>
  );
};

export default Button;
